import { supabase } from './supabase'
import { logLogin, logLoginAttempt } from './services/auditService'

export const ROLES = {
  ADMIN: 'admin',
  STORE_ADMIN: 'store_admin',
  STAFF: 'staff',
}

export const ROLE_LABELS = {
  admin: '管理员',
  store_admin: '店管理员',
  staff: '门店员工',
}

export const SUPER_ADMIN_EMAILS = ['admin@peripheral.local', '1562902842@qq.com']

let _defaultStaffHashed = null

/** 预置员工及哈希（与 useStaffConfig 默认账号一致） */
export async function getDefaultStaffHashed() {
  if (_defaultStaffHashed) return _defaultStaffHashed
  const [admin, staff1, staff2, staff3] = await Promise.all([
    hashPassword('admin123'),
    hashPassword('staff123'),
    hashPassword('staff123'),
    hashPassword('staff123'),
  ])
  _defaultStaffHashed = [
    { email: 'admin@peripheral.local', display_name: '管理员', role: ROLES.ADMIN, hotel: null, hashed_password: admin },
    { email: 'staff@peripheral.local', display_name: '苏州店员工', role: ROLES.STAFF, hotel: '苏州店', hashed_password: staff1 },
    { email: 'staff-nb@peripheral.local', display_name: '宁波店员工', role: ROLES.STAFF, hotel: '宁波店', hashed_password: staff2 },
    { email: 'staff-hz@peripheral.local', display_name: '杭州店员工', role: ROLES.STAFF, hotel: '杭州店', hashed_password: staff3 },
  ]
  return _defaultStaffHashed
}

function staffToSession(row, email) {
  return {
    email,
    role: row.role,
    hotel: row.hotel,
    displayName: row.display_name,
  }
}

// ==================== 密码哈希工具（Web Crypto API, SHA-256 + salt）====================

function generateSalt() {
  const salt = new Uint8Array(16)
  crypto.getRandomValues(salt)
  return Array.from(salt, (b) => b.toString(16).padStart(2, '0')).join('')
}

export async function hashPassword(password, salt) {
  const usedSalt = salt || generateSalt()
  const encoder = new TextEncoder()
  const data = encoder.encode(usedSalt + password)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${usedSalt}:${hashHex}`
}

export async function verifyPassword(password, hashed) {
  if (!password || !hashed) return false
  const [salt] = hashed.split(':')
  if (!salt) return false
  const computed = await hashPassword(password, salt)
  return computed === hashed
}

// ==================== 用户会话管理 ====================

const LS_USER_KEY = 'user'
const LS_STAFF_KEY = 'peri_staff_config'

let usePersistentStorage = true

/** 登录前调用：true = 记住登录（localStorage）；false = sessionStorage */
export function setRememberSession(remember) {
  usePersistentStorage = remember !== false
}

function activeUserStore() {
  return usePersistentStorage ? localStorage : sessionStorage
}

export function getCurrentUser() {
  const tryParse = (raw) => {
    try {
      return JSON.parse(raw || 'null')
    } catch {
      return null
    }
  }
  return tryParse(localStorage.getItem(LS_USER_KEY)) || tryParse(sessionStorage.getItem(LS_USER_KEY))
}

export function setCurrentUser(user) {
  const store = activeUserStore()
  store.setItem(LS_USER_KEY, JSON.stringify(user))
  const other = usePersistentStorage ? sessionStorage : localStorage
  other.removeItem(LS_USER_KEY)
}

export function clearCurrentUser() {
  localStorage.removeItem(LS_USER_KEY)
  sessionStorage.removeItem(LS_USER_KEY)
}

export function isAdmin(user = getCurrentUser()) {
  return user?.role === ROLES.ADMIN
}

export function isSuperAdmin(user = getCurrentUser()) {
  return isAdmin(user)
}

export function isStoreAdmin(user = getCurrentUser()) {
  return user?.role === ROLES.STORE_ADMIN
}

export function isStaff(user = getCurrentUser()) {
  return user?.role === ROLES.STAFF
}

export function canManage(user = getCurrentUser()) {
  return canManageStore(user)
}

export function canManageGlobally(user = getCurrentUser()) {
  return isSuperAdmin(user)
}

export function canManageStore(user = getCurrentUser()) {
  return isSuperAdmin(user) || isStoreAdmin(user)
}

export function canManageStaff(user = getCurrentUser()) {
  return canManageStore(user)
}

export function isReadOnly(user = getCurrentUser()) {
  return isStaff(user)
}

export function canAccessHotel(user = getCurrentUser(), hotel = null) {
  if (isSuperAdmin(user)) return true
  if (!hotel) return false
  return user?.hotel === hotel
}

export function assertAssetInScope(user = getCurrentUser(), hotel = null) {
  if (canAccessHotel(user, hotel)) return
  throw new Error('无权操作该门店设备')
}

export function filterAssetsByScope(assets, user = getCurrentUser()) {
  if (!user?.hotel || isSuperAdmin(user)) return assets
  return assets.filter((a) => a.hotel === user.hotel)
}

export function filterStaffByScope(staffList, user = getCurrentUser()) {
  if (isSuperAdmin(user)) return staffList
  if (!isStoreAdmin(user) || !user?.hotel) return []
  return staffList.filter((row) => row.role === ROLES.STAFF && row.hotel === user.hotel)
}

export function isProtectedSuperAdmin(email = '') {
  return SUPER_ADMIN_EMAILS.includes(String(email).trim().toLowerCase())
}

// ==================== 认证（员工配置 + 密码哈希，无需 Supabase Auth）====================

async function findStaffForLogin(email) {
  try {
    const { data, error } = await supabase
      .from('staff_config')
      .select('email, display_name, role, hotel, hashed_password')
      .eq('email', email)
      .maybeSingle()
    if (!error && data) return data
  } catch { /* 表未就绪 */ }

  const cached = JSON.parse(localStorage.getItem(LS_STAFF_KEY) || 'null')
  const defaults = await getDefaultStaffHashed()
  return (cached || defaults).find((s) => s.email === email) || null
}

/**
 * 使用员工配置中的邮箱与密码登录
 */
export async function signIn(email, password, remember = true) {
  const key = email?.trim().toLowerCase()
  if (!key || !password) {
    throw new Error('请输入邮箱和密码')
  }

  setRememberSession(remember)

  const row = await findStaffForLogin(key)
  if (!row) {
    await logLoginAttempt(key, { success: false, message: '邮箱未登记' })
    throw new Error('邮箱或密码错误')
  }

  let hash = row.hashed_password
  if (!hash) {
    const defaults = await getDefaultStaffHashed()
    const def = defaults.find((s) => s.email === key)
    hash = def?.hashed_password || ''
  }

  if (!hash || !(await verifyPassword(password, hash))) {
    await logLoginAttempt(key, { success: false, message: '密码错误' })
    throw new Error('邮箱或密码错误')
  }

  const session = staffToSession(row, key)
  setCurrentUser(session)
  await logLogin(session, { success: true })
  return session
}

/** @deprecated 使用 signIn */
export const signInWithSupabase = signIn

export async function signOut() {
  clearCurrentUser()
}
